"use client";

import { useIsLoggedInRetailer } from "@faire/deprecated-retailer-visitor-shared/lib/isLoggedInRetailer";
import {
  assignInlineVars,
  DynamicVariable,
} from "@faire/deprecated-retailer-visitor-shared/lib/tailwindUtils";
import {
  useSettingEnableBrandHoverCard,
  useAssignSettingEnableBrandHoverCard,
} from "@faire/deprecated-retailer-visitor-shared/settings/useSettingEnableBrandHoverCard";
import { Core } from "@faire/slate/foundation/color";
import { useQuery as useBrandHoverInfoQuery } from "@faire/web-api--source/endpoints/www/api/v3/layout/product-hover-brand-info/brandToken/get-hooks";
import { trackProductTileBrandHoverCardActionCategoryClick } from "@faire/web-api--source/events/productTile/actionCategoryClick/brandHoverCard";
import { trackProductTileBrandHoverCardActionCategoryImpression } from "@faire/web-api--source/events/productTile/actionCategoryImpression/brandHoverCard";
import {
  optimizeImageUrl,
  optimizeImageUrlFillSquare,
} from "@faire/web/common/images/optimizeImages";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { Column, Row } from "@faire/web/slate/Layout";
import { Typography } from "@faire/web/slate/Typography";
import { Popover } from "@faire/web/slate/utils/Popover/Popover";
import { PopoverContent } from "@faire/web/slate/utils/Popover/PopoverContent";
import { cn } from "@faire/web/tools/cn";
import { usePathname } from "next/navigation";
import React, { useState, useCallback, useEffect, useMemo } from "react";

const IMAGE_WIDTH = 64;
const IMAGE_WIDTH_SMALL = 56;
const VERTICAL_IMAGE_RATIO = 4 / 3;
const OUTER_HORIZONTAL_PADDING = 12;
const INNER_HORIZONTAL_PADDING = 2;

const MIN_IMAGE_COUNT = 2;
const MAX_IMAGE_COUNT = 3;

enum ClickTarget {
  PRODUCT_IMAGE = "PRODUCT_IMAGE",
  TEXT = "TEXT",
}

const varImageWidth: DynamicVariable<"--f-bhc-image-width"> = {
  variable: "--f-bhc-image-width",
  className: "w-(--f-bhc-image-width)",
};

const varVerticalImageHeight: DynamicVariable<"--f-bhc-vertical-image-height"> =
  {
    variable: "--f-bhc-vertical-image-height",
    className: "h-(--f-bhc-vertical-image-height)",
  };

const varPopoverWidth: DynamicVariable<"--f-bhc-popover-width"> = {
  variable: "--f-bhc-popover-width",
  className: "w-(--f-bhc-popover-width)",
};

const varPopoverOpacity: DynamicVariable<"--f-bhc-popover-opacity"> = {
  variable: "--f-bhc-popover-opacity",
  className: "opacity-(--f-bhc-popover-opacity)",
};

const varTransitionDuration: DynamicVariable<"--f-bhc-transition-duration"> = {
  variable: "--f-bhc-transition-duration",
  className: "duration-(--f-bhc-transition-duration)",
};

const varTransitionTiming: DynamicVariable<"--f-bhc-transition-timing"> = {
  variable: "--f-bhc-transition-timing",
  className: "ease-(--f-bhc-transition-timing)",
};

export interface BrandHoverCardData {
  displayVerticalImages?: boolean;
  brandToken?: string;
  impressionId?: string;
  productToken?: string;
}

interface BrandHoverCardProps {
  children: React.ReactNode;
  brandHoverData?: BrandHoverCardData;
}

export const BrandHoverCard: React.FC<BrandHoverCardProps> = ({
  children,
  brandHoverData,
}) => {
  const pathname = usePathname();
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const [isVisible, setIsVisible] = useState(false);

  // Controls when the popover is rendered in the DOM (for animation timing)
  // while isVisible controls the visual opacity state
  const [isMounted, setIsMounted] = useState(false);

  const closeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const delayAssigmentAndVisibilityTimeoutRef = React.useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const unmountTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const openTimestampRef = React.useRef<number | null>(null);

  const isBrandHoverCardEnabled = useSettingEnableBrandHoverCard();
  const assignSetting = useAssignSettingEnableBrandHoverCard();

  // Only fetch brand hover data when the card is mounted, XP is enabled, and we have a brand token
  const shouldFetchData =
    isMounted &&
    isBrandHoverCardEnabled &&
    !!brandHoverData?.brandToken &&
    isLoggedInRetailer;

  const { data: brandHoverInfo } = useBrandHoverInfoQuery(
    brandHoverData?.brandToken ?? "",
    {
      enabled: shouldFetchData,
      staleTime: Infinity,
    }
  );

  // Get product images from API data
  const productImages = useMemo(() => {
    if (brandHoverInfo?.brand_hover_products) {
      return brandHoverInfo.brand_hover_products
        .filter(
          (product) => product.product_token !== brandHoverData?.productToken
        )
        .map((product) => product.image)
        .filter(isNotUndefined)
        .slice(0, MAX_IMAGE_COUNT);
    }
    return [];
  }, [brandHoverInfo?.brand_hover_products, brandHoverData?.productToken]);

  const imageWidth = useMemo(() => {
    // Using (pathname || "") to avoid routing errors in Storybook and unrelated tests
    return (pathname ?? "").includes("/category") ||
      (pathname ?? "").includes("/search")
      ? IMAGE_WIDTH
      : IMAGE_WIDTH_SMALL;
  }, [pathname]);

  const imageUrls = useMemo(
    () =>
      productImages
        .map((image) =>
          brandHoverData?.displayVerticalImages
            ? optimizeImageUrl(image, false, {
                width: imageWidth,
                height: imageWidth * VERTICAL_IMAGE_RATIO,
                resizeMethod: "fill",
              })
            : optimizeImageUrlFillSquare(image, false, imageWidth)
        )
        .filter(isNotUndefined),
    [productImages, brandHoverData?.displayVerticalImages, imageWidth]
  );

  // Only show content if we have enough images, the brand has enough products,
  // and the setting is enabled
  const shouldShowContent =
    isBrandHoverCardEnabled &&
    brandHoverInfo &&
    imageUrls.length >= MIN_IMAGE_COUNT &&
    brandHoverInfo.active_product_count &&
    brandHoverInfo.active_product_count >= MIN_IMAGE_COUNT;

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const clearEntryDelayTimeout = useCallback(() => {
    if (delayAssigmentAndVisibilityTimeoutRef.current) {
      clearTimeout(delayAssigmentAndVisibilityTimeoutRef.current);
      delayAssigmentAndVisibilityTimeoutRef.current = null;
    }
  }, []);

  const clearUnmountTimeout = useCallback(() => {
    if (unmountTimeoutRef.current) {
      clearTimeout(unmountTimeoutRef.current);
      unmountTimeoutRef.current = null;
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    clearCloseTimeout();
    clearEntryDelayTimeout();
    clearUnmountTimeout();

    if (!isVisible) {
      openTimestampRef.current = Date.now();

      // First render the element in DOM with opacity 0
      setIsMounted(true);

      // After 200ms delay: trigger opacity transition AND call assignment
      delayAssigmentAndVisibilityTimeoutRef.current = setTimeout(() => {
        setIsVisible(true);
        assignSetting();
        delayAssigmentAndVisibilityTimeoutRef.current = null;
      }, 200);
    }
  }, [
    isVisible,
    clearCloseTimeout,
    clearEntryDelayTimeout,
    clearUnmountTimeout,
    assignSetting,
  ]);

  const handleMouseLeave = useCallback(() => {
    clearCloseTimeout();
    clearEntryDelayTimeout();

    // Using a timeout of 100ms to prevent the card from closing immediately when the mouse
    // travels between the trigger and the popup content.
    closeTimeoutRef.current = setTimeout(() => {
      const closeTimestamp = Date.now();

      if (
        openTimestampRef.current &&
        brandHoverData?.impressionId &&
        brandHoverData?.productToken &&
        brandHoverData?.brandToken &&
        shouldShowContent &&
        isVisible
      ) {
        const duration = closeTimestamp - openTimestampRef.current;
        const productTokens =
          brandHoverInfo?.brand_hover_products
            ?.map((product) => product.product_token)
            .filter(
              (productToken) => productToken !== brandHoverData?.productToken
            )
            .filter(isNotUndefined)
            .slice(0, MAX_IMAGE_COUNT)
            .join(" ") || "";

        trackProductTileBrandHoverCardActionCategoryImpression(
          brandHoverData.impressionId,
          brandHoverData.productToken,
          brandHoverData.brandToken,
          productTokens,
          duration.toString(),
          ""
        );
      }

      setIsVisible(false);

      // Wait for the exit animation to complete (100ms), then unmount
      unmountTimeoutRef.current = setTimeout(() => {
        setIsMounted(false);
        unmountTimeoutRef.current = null;
      }, 100);

      openTimestampRef.current = null;
    }, 100);
  }, [
    brandHoverData?.impressionId,
    brandHoverData?.productToken,
    brandHoverData?.brandToken,
    brandHoverInfo,
    clearEntryDelayTimeout,
    isVisible,
    shouldShowContent,
  ]);

  // Width of the popover is the width of the images + the padding.
  // This needs to be set explcitly because the popover implementation can't calculate the width automatically.
  const popoverWidth = useMemo(() => {
    return imageUrls.length === 0
      ? 0
      : imageUrls.length * imageWidth +
          OUTER_HORIZONTAL_PADDING * 2 +
          (imageUrls.length - 1) * INNER_HORIZONTAL_PADDING;
  }, [imageUrls.length, imageWidth]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      if (delayAssigmentAndVisibilityTimeoutRef.current) {
        clearTimeout(delayAssigmentAndVisibilityTimeoutRef.current);
      }
      if (unmountTimeoutRef.current) {
        clearTimeout(unmountTimeoutRef.current);
      }
    };
  }, []);

  if (!brandHoverData || !isLoggedInRetailer) {
    return <>{children}</>;
  }

  const handleClick = (clickTarget: ClickTarget) => {
    if (
      brandHoverData?.impressionId &&
      brandHoverData?.productToken &&
      brandHoverData?.brandToken
    ) {
      trackProductTileBrandHoverCardActionCategoryClick(
        brandHoverData.impressionId,
        brandHoverData.productToken,
        brandHoverData.brandToken,
        clickTarget,
        ""
      );
    }
  };

  return (
    <Popover open={isMounted} placement="bottom-start">
      <div
        className="inline-block"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
      <PopoverContent>
        {shouldShowContent ? (
          <div
            data-test-id="brand-hover-card-content"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className={cn(
              "rounded-lg border border-solid border-[#00000014] bg-[white] p-3 shadow-[0px_2px_10px_0px_#00000026]",
              "transition-opacity",
              varPopoverWidth.className,
              varPopoverOpacity.className,
              varTransitionDuration.className,
              varTransitionTiming.className
            )}
            style={assignInlineVars({
              [varPopoverWidth.variable]: `${popoverWidth}px`,
              [varPopoverOpacity.variable]: isVisible ? "1" : "0",
              [varTransitionDuration.variable]: isVisible ? "150ms" : "100ms",
              [varTransitionTiming.variable]: isVisible ? "out" : "in",
            })}
          >
            <Column gap={8}>
              <Row gap={INNER_HORIZONTAL_PADDING}>
                {imageUrls.map((imageUrl, index) => (
                  <ProductImageThumbnail
                    key={index}
                    imageUrl={imageUrl}
                    isVerticalImage={brandHoverData?.displayVerticalImages}
                    onClick={() => handleClick(ClickTarget.PRODUCT_IMAGE)}
                    imageWidth={imageWidth}
                  />
                ))}
              </Row>

              {brandHoverInfo.catalog_display_text ? (
                <Typography
                  variant="labelSansRegular"
                  color={Core.text.subdued}
                  style={{ textAlign: "left" }}
                  onClick={() => handleClick(ClickTarget.TEXT)}
                >
                  {brandHoverInfo.catalog_display_text}
                </Typography>
              ) : null}
            </Column>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
};

const ProductImageThumbnail: React.FC<{
  imageUrl: string;
  isVerticalImage?: boolean;
  onClick?: () => void;
  imageWidth: number;
}> = ({ imageUrl, isVerticalImage, onClick, imageWidth }) => {
  return (
    <img
      data-test-id="brand-hover-card-product-image"
      src={imageUrl}
      className={cn(
        `w-(--f-bhc-image-width) object-cover`,
        isVerticalImage
          ? `h-(--f-bhc-vertical-image-height)`
          : `h-(--f-bhc-image-width)`
      )}
      onClick={onClick}
      style={assignInlineVars({
        [varImageWidth.variable]: `${imageWidth}px`,
        [varVerticalImageHeight.variable]: `${imageWidth * VERTICAL_IMAGE_RATIO}px`,
      })}
    />
  );
};
